import { useState, useRef, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const contactMethods = [
  {
    icon: 'ri-phone-line',
    title: '24/7 Emergency Line',
    description: 'For urgent water, fire, or storm damage — call us any time, day or night. We answer every call.',
    value: '(209) 451-2288',
    href: 'tel:2094512288',
    cta: 'Call Now',
    color: 'bg-teal-50 border-teal-200',
    iconBg: 'bg-teal-600',
  },
  {
    icon: 'ri-mail-line',
    title: 'Email Us',
    description: "Send us a message and we'll respond within a few hours during business hours.",
    value: 'info@mercedwaterrestoration.com',
    href: 'mailto:info@mercedwaterrestoration.com',
    cta: 'Send Email',
    color: 'bg-slate-50 border-slate-200',
    iconBg: 'bg-slate-700',
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Visit Our Office',
    description: "We're locally based in Merced and proudly serve all of Merced County and the Central Valley.",
    value: '1729 U St, Merced, CA 95340',
    href: 'https://maps.google.com/?q=1729+U+St+Merced+CA+95340',
    cta: 'Get Directions',
    color: 'bg-orange-50 border-orange-200',
    iconBg: 'bg-orange-500',
  },
  {
    icon: 'ri-time-line',
    title: 'Business Hours',
    description: 'Our office team is available during regular hours. Emergency crews are available 24/7.',
    value: 'Mon–Fri: 8am–6pm | Emergency: 24/7',
    href: null,
    cta: null,
    color: 'bg-green-50 border-green-200',
    iconBg: 'bg-green-600',
  },
];

const serviceAreas = [
  'Merced', 'Atwater', 'Los Banos', 'Livingston', 'Turlock',
  'Modesto', 'Fresno', 'Madera', 'Chowchilla', 'Gustine',
];

export default function ContactPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Indoor-Restore Services",
        "telephone": "+12094512288",
        "email": "info@mercedwaterrestoration.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1729 U St",
          "addressLocality": "Merced",
          "addressRegion": "CA",
          "postalCode": "95340",
          "addressCountry": "US"
        },
        "url": import.meta.env.VITE_SITE_URL,
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+12094512288",
          "contactType": "Emergency Service",
          "availableLanguage": "English",
          "areaServed": "Merced County, CA"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setStatus('sending');

    const body = new URLSearchParams();
    Object.entries(formData).forEach(([key, val]) => body.append(key, val));

    try {
      const res = await fetch('https://readdy.ai/api/form/d6qh58kddmmni7ck8h1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        setCharCount(0);
        formRef.current?.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative pt-20 overflow-hidden">
        <div className="absolute inset-0 h-80">
          <img
            src="https://public.readdy.ai/ai/img_res/cc3d27b208c35f60585303d4fda901d1.jpg"
            alt="Contact Indoor-Restore Services Merced CA"
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-white"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32 text-center">
          <span className="inline-block bg-teal-600/90 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">
            We&apos;re Here to Help
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Get in Touch with<br />Indoor-Restore Services
          </h1>
          <p className="text-white/85 text-lg max-w-2xl mx-auto">
            Whether it&apos;s an emergency or you&apos;re planning ahead — our Merced-based team is ready to respond fast, answer your questions, and get your property back to normal.
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10 mb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {contactMethods.map((method) => (
            <div
              key={method.title}
              className={`rounded-xl border p-6 flex flex-col gap-4 ${method.color} shadow-sm hover:shadow-md transition-shadow`}
            >
              <div className={`w-12 h-12 ${method.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <i className={`${method.icon} text-white text-xl`}></i>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-1">{method.title}</h3>
                <p className="text-gray-500 text-sm mb-3 leading-relaxed">{method.description}</p>
                <p className="text-gray-800 text-sm font-semibold">{method.value}</p>
              </div>
              {method.href && method.cta && (
                <a
                  href={method.href}
                  target={method.href.startsWith('http') ? '_blank' : undefined}
                  rel={method.href.startsWith('http') ? 'nofollow noopener' : undefined}
                  className="inline-flex items-center gap-1.5 text-teal-700 text-sm font-semibold hover:text-teal-900 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {method.cta} <i className="ri-arrow-right-line text-base"></i>
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Main Content: Form + Info */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

          {/* Left: Info */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Why Reach Out to Us?</h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                Indoor-Restore Services has been the trusted name in <strong>water damage restoration</strong> across Merced County. When disaster strikes, you need a team that responds fast, communicates clearly, and delivers results.
              </p>
              <ul className="space-y-3">
                {[
                  { icon: 'ri-shield-check-line', text: 'Licensed, insured & IICRC certified professionals' },
                  { icon: 'ri-time-line', text: '60-minute average response time in Merced' },
                  { icon: 'ri-file-list-3-line', text: 'Free estimates with no obligation' },
                  { icon: 'ri-customer-service-2-line', text: 'Direct insurance billing assistance' },
                  { icon: 'ri-map-pin-2-line', text: 'Locally owned & operated in Merced, CA' },
                  { icon: 'ri-star-line', text: '5-star rated across Google & Yelp' },
                ].map((item) => (
                  <li key={item.text} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-teal-600 text-base`}></i>
                    </div>
                    <span className="text-gray-600 text-sm">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Service Areas */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-2-line text-teal-600"></i>
                </div>
                Areas We Serve
              </h3>
              <p className="text-gray-500 text-sm mb-4">We proudly serve Merced County and surrounding communities throughout the Central Valley.</p>
              <div className="flex flex-wrap gap-2">
                {serviceAreas.map((area) => (
                  <span key={area} className="bg-white border border-slate-200 text-gray-700 text-xs px-3 py-1 rounded-full font-medium">
                    {area}
                  </span>
                ))}
              </div>
            </div>

            {/* Emergency CTA */}
            <div className="bg-teal-600 rounded-xl p-6 text-white">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-alarm-warning-line text-white text-xl"></i>
              </div>
              <h3 className="font-bold text-lg mb-2">Dealing with an Emergency?</h3>
              <p className="text-teal-100 text-sm mb-4 leading-relaxed">
                Don&apos;t wait — water damage spreads fast. Call our emergency line right now for immediate dispatch.
              </p>
              <a
                href="tel:2094512288"
                className="inline-flex items-center gap-2 bg-white text-teal-700 font-bold px-5 py-2.5 rounded-lg hover:bg-teal-50 transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                (209) 451-2288
              </a>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Send Us a Message</h2>
              <p className="text-gray-500 text-sm mb-8">
                Fill out the form below and a member of our team will get back to you promptly. A confirmation will be sent to your email.
              </p>

              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-check-line text-teal-600 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-gray-500 text-sm max-w-sm">
                    Thank you for reaching out. We&apos;ve received your message and will be in touch shortly. A confirmation has been sent to your email.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-6 bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="contact-page-form"
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Phone Number
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(209) 555-0000"
                        className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                      />
                    </div>
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Service Needed
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition bg-white cursor-pointer"
                      >
                        <option value="">Select a service...</option>
                        <option value="Water Damage Restoration">Water Damage Restoration</option>
                        <option value="Flood Damage">Flood Damage</option>
                        <option value="Mold Remediation">Mold Remediation</option>
                        <option value="Fire & Smoke Damage">Fire &amp; Smoke Damage</option>
                        <option value="Storm Damage">Storm Damage</option>
                        <option value="Commercial Restoration">Commercial Restoration</option>
                        <option value="Reconstruction">Reconstruction</option>
                        <option value="Other">Other / Not Sure</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Tell Us About Your Situation <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Describe the damage, location, and any urgent details..."
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition resize-none"
                    />
                    <div className="flex justify-between items-center mt-1">
                      <span className={`text-xs ${charCount >= 500 ? 'text-red-500' : 'text-gray-400'}`}>
                        {charCount}/500 characters
                      </span>
                      {charCount >= 500 && (
                        <span className="text-xs text-red-500">Character limit reached</span>
                      )}
                    </div>
                  </div>

                  {status === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-error-warning-line"></i>
                      </div>
                      Something went wrong. Please try again or call us directly.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || charCount > 500}
                    className="w-full bg-teal-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {status === 'sending' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Send Message
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-400 text-center">
                    By submitting this form, you agree to be contacted by Indoor-Restore Services regarding your inquiry. We respect your privacy.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="mb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Find Us in Merced</h2>
          <p className="text-gray-500 text-sm">Centrally located to serve all of Merced County and the greater Central Valley.</p>
        </div>
        <div className="w-full h-80 rounded-none overflow-hidden shadow-inner">
          <iframe
            title="Indoor-Restore Services Location - Merced CA"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3152.0!2d-120.4829!3d37.3022!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8090a8e3b0000001%3A0x1!2s1729+U+St%2C+Merced%2C+CA+95340!5e0!3m2!1sen!2sus!4v1700000000000"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </section>

      <Footer />
    </div>
  );
}
